import { lastUtils } from '@last/core'

type Product = {
  id: string
  name: string
  quantity: number
  price: number
  finalPrice?: number
}

type User = {
  name: string
  email: string
  phoneNumber: string
  id?: string
  customerId?: string
}

export default class metaAPI {
  private accessToken: string
  private url: string
  private getUser: () => User
  private ip: Promise<string>

  constructor(pixelId: string, accessToken: string, router: any, userGetter: () => User) {
    this.accessToken = accessToken
    this.getUser = userGetter
    this.url = `https://graph.facebook.com/v22.0/${pixelId}/events`
    this.trackPageViews(router)
    this.ip = fetch('https://api.ipify.org').then(res => res.text())
  }

  isAvailable() {
    return true
  }

  trackPageViews(router: any) {
    this.sendEvent('PageView')
    router.afterEach(() => {
      this.sendEvent('PageView')
    })
  }

  async checkoutCompleted({
    products,
    totalPrice,
    currencyCode
  }: {
    products: Product[]
    totalPrice: number
    currencyCode: string
  }) {
    this.sendEvent('Purchase', {
      contents: mapProducts(products),
      content_type: 'product',
      currency: currencyCode || 'EUR',
      value: lastUtils.round(totalPrice / 100)
    })
  }

  async beginCheckout({
    products,
    totalPrice,
    currencyCode
  }: {
    products: Product[]
    totalPrice: number
    currencyCode: string
  }) {
    const mappedProducts = mapProducts(products)
    this.sendEvent('InitiateCheckout', {
      content_ids: mappedProducts.map(p => p.id),
      contents: mappedProducts,
      currency: currencyCode,
      num_items: mappedProducts.map(p => p.quantity).reduce((a, b) => a + b),
      value: lastUtils.round(totalPrice / 100)
    })
  }

  async addProductToCart({ product, currencyCode }: { product: Product; currencyCode: string }) {
    const item = mapProducts([product])[0]
    this.sendEvent('AddToCart', {
      contents: [item],
      content_type: 'product',
      currency: currencyCode,
      value: item.value
    })
  }

  async itemViewed({ item, currencyCode }: { item: Product; currencyCode: string }) {
    const mappedItem = mapProducts([item])[0]
    this.sendEvent('ViewContent', {
      content_id: mappedItem.id,
      content_name: mappedItem.name,
      currency: currencyCode,
      value: mappedItem.value
    })
  }

  async bookingOnGoing() {
    this.sendEvent('BookingOnGoing')
  }

  async bookingDone() {
    this.sendEvent('BookingDone')
  }

  newLead() {
    this.sendEvent('Lead')
    this.sendEvent('LeadWebform') // Duplicate custom event
  }

  private async sendEvent(name: string, data?: Record<string, any>) {
    const userData = await this.getUserData()
    if (!userData) return
    const eventData = {
      event_name: name,
      event_time: Math.floor(Date.now() / 1000),
      custom_data: data,
      user_data: userData,
      event_source_url: window.location.href,
      action_source: 'website'
    }

    const formData = new FormData()
    formData.append('access_token', this.accessToken)
    formData.append('data', JSON.stringify([eventData]))

    await fetch(this.url, {
      method: 'POST',
      body: formData
    })
  }

  private async getUserData() {
    const { name, email, phoneNumber, id: userId, customerId } = this.getUser()
    if (!email || !name) return
    const [fn, em, ph, external_id, client_ip_address] = await Promise.all([
      sha256(name?.toLowerCase()),
      sha256(email),
      sha256(phoneNumber),
      sha256(userId ?? customerId ?? ''),
      this.ip
    ])
    return { fn, em, ph, external_id, client_ip_address }
  }
}

function mapProducts(
  products: Product[]
): { id: string; name: string; quantity: number; value: number }[] {
  return products.map(product => {
    let price = product.finalPrice
    if (!price) price = product.price
    return {
      id: product.id,
      name: product.name,
      quantity: product.quantity,
      value: lastUtils.round(price / 100)
    }
  })
}

// https://stackoverflow.com/questions/18338890/are-there-any-sha-256-javascript-implementations-that-are-generally-considered-t
async function sha256(message: string) {
  if (!message) return ''
  const msgBuffer = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}
